namespace Portico.Services {
    import Inject = Portico.Decorators.Inject;
    import Service = Portico.Decorators.Service;
    import pme = Portico.Models.Enterprise;
    import ps = Portico.Services;
    import psga = apigateway.client;
    import psgm = apigateway.models;

    export interface IMemberNumberCodeResponse {
        code: number;
    }
    export class MemberNumberCodeResponse {
        code: number;
        constructor(obj?: IMemberNumberCodeResponse) {
            this.code = null;
            if (obj) {
                this.code = obj.code || null;
            }
        }
    }
    export interface MemberNumberCodeResponse extends psgm.CardMemberNumberStatusResultModel {}

    export interface ICouponService {
        basicEnrollment(
            programId: number,
            account: pme.Account,
            enrollment: pme.Enrollment
        ): angular.IPromise<psgm.EnrollmentResultModel>;
        getEnrollmentDetails(
            programId: number,
            patient: pme.Patient
        ): angular.IPromise<psgm.CouponGetEnrollmentDetailsDefaultResponseData>;
        memberNumberStatus(
            programId: number,
            memberNumber: string
        ): angular.IPromise<IMemberNumberCodeResponse>;
        verifySurveyAnswer(
            programId: number,
            surveyId: number,
            questionId: number,
            account: pme.Account
        ): angular.IPromise<boolean>;
        verifySurveyAnswers(
            programId: number,
            surveyIds: number[],
            questionIds: number[],
            account: pme.Account
        ): angular.IPromise<boolean>;
    }

    @Service('app', 'couponService')
    @Inject(
        '$q',
        'dataService',
        'dateService',
        'EdgeCouponApi',
        'EnrollmentExtV2Api',
        'CardExtV1Api'
    )
    export class CouponService implements ICouponService {
        constructor(
            private $q: angular.IQService,
            private dataService: ps.IDataService,
            private dateService: ps.IDateService,
            private edgeCouponApi: psga.EdgeCouponApi,
            private extEnrollmentApi: psga.EnrollmentExtV2Api,
            private extCardApi: psga.CardExtV1Api
        ) {}

        memberNumberStatus(
            programId: number,
            memberNumber: string
        ): angular.IPromise<IMemberNumberCodeResponse> {
            if (
                this.dataService.session.mode === 'edit' ||
                this.dataService.session.mode === 'mock'
            ) {
                return this.$q.when(new MemberNumberCodeResponse());
            }

            return this.$q.when(this.extCardApi.memberNumberStatusGet(programId, memberNumber).then((response) => {
                const value = <MemberNumberCodeResponse>response.data.data;

                if (response.data.data.cardEndDate) {
                    if (this.dateService.isAfter(response.data.data.cardEndDate)) {
                        // expired
                        value.code = LegacyCheckMNCodes.Expired;
                    } else {
                        // already active
                        value.code = LegacyCheckMNCodes.Activated;
                    }
                } else {
                    // inactive -> proceed
                    value.code = LegacyCheckMNCodes.Inactive;
                }

                return value;
            }));
        }

        getEnrollmentDetails(programId: number, patient: pme.Patient) {
            const { firstName, lastName, dateOfBirth } = patient;
            const request = {
                programId,
                firstName,
                lastName,
                dateOfBirth: moment(dateOfBirth)
            } as psgm.GetEnrollmentDetailsRequestModel;
            
            if (patient.addresses && patient.addresses.length > 0) {
                request.city = patient.addresses[0].city;
                request.state = patient.addresses[0].state;
                request.zip = patient.addresses[0].zip;
            }

            return this.$q.when(this.edgeCouponApi.couponGetEnrollmentDetails(request).then(response => {
                return response.data.data;
            }));
        }

        basicEnrollment(programId: number, account: pme.Account, enrollment: pme.Enrollment) {
            const accountSessions = account.accountSessions.filter(accountSession => {
                return !accountSession.id && accountSession.surveySession;
            });
            const surveySession =
                accountSessions && accountSessions.length > 0
                    ? accountSessions[0].surveySession
                    : null;

            const requestModel = {} as psgm.EnrollmentRequestModel;
            const mapper = new Mappers.SurveyMapper();

            requestModel.survey = mapper.toLegacy(surveySession);
            requestModel.demographics = mapper.toCouponDemographics(
                account,
                enrollment.memberNumber
            );

            requestModel.clientIdentifiers = null;

            return this.$q.when(this.extEnrollmentApi.basicEnrollmentCreate(programId, requestModel).then(response => {
                return response.data.data;
            }));
        }

        enrollment(programId: number, account: pme.Account, enrollment: pme.Enrollment) {
            const accountSessions = account.accountSessions.filter(accountSession => {
                return !accountSession.id && accountSession.surveySession;
            });
            const surveySession =
                accountSessions && accountSessions.length > 0
                    ? accountSessions[0].surveySession
                    : null;

            const requestModel = {} as psgm.EnrollmentInsVerRequestModel;
            const mapper = new Mappers.SurveyMapper();
            requestModel.survey = mapper.toLegacy(surveySession);
            requestModel.demographics = mapper.toCouponDemographics(
                account,
                enrollment.memberNumber
            );
            requestModel.clientIdentifiers = null;
            return this.extEnrollmentApi
                .enrollmentCreate(programId, requestModel)
                .then(response => {
                    return response;
                });
        }

        verifySurveyAnswer(
            programId: number,
            surveyId: number,
            questionId: number,
            account: pme.Account
        ): angular.IPromise<boolean> {
            if (
                this.dataService.session.mode === 'edit' ||
                this.dataService.session.mode === 'mock'
            ) {
                return this.$q.when(true);
            }

            let mapper = new ps.Mappers.SurveyMapper();
            let surveySession = account.getSurveySession(surveyId);
            let answers = surveySession.getSurveySessionAnswers(questionId);
            let legacyQuestion = mapper.toLegacySurveyQuestion(
                surveySession.surveyId,
                questionId,
                answers[0]
            );

            return this.$q.when(this.extEnrollmentApi.verifySurveyAnswerCreate(programId, legacyQuestion).then((response) => {
                return response.data.data.isEligible;
            }));
        }

        verifySurveyAnswers(
            programId: number,
            surveyIds: number[],
            questionIds: number[],
            account: pme.Account
        ): angular.IPromise<boolean> {
            let promises: angular.IPromise<boolean>[] = [];

            for (let i = 0; i < surveyIds.length; i++) {
                promises.push(this.verifySurveyAnswer(programId, surveyIds[i], questionIds[i], account));
            }

            return this.$q.all(promises).then(x => x.every(z => z));
        }
    }
    export enum LegacyCheckMNCodes {
        Invalid = 0, // Failure. Invalid member number For program (success - false)
        Expired = 1, // Have to check the date with dateService.isAfter(cardEndDate) (success - true)
        Activated = 2, // Have to check the cardActivationDate!=null (success - true)
        Inactive = 3, // Have to check the cardActivationDate=null && cardEndDate=null? (succes - true)
        Error = 4, // Generic error response message is something like "an unknown error occurred" (success - false)
    }
}
